import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Save,
  User,
  Phone,
  Mail,
  Calendar,
  DollarSign,
  FileText,
  AlertCircle,
  Heart,
  Users,
  Plus,
  Trash2,
  ChevronDown,
  ChevronUp,
  Dog,
  UserPlus,
  Sparkles,
  Edit2,
  X,
  Download,
  Loader2
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tipos de referidos internos con sus configuraciones
const TIPOS_REFERIDO_INTERNO = [
  { 
    value: 'novio', 
    label: 'Novio', 
    comision: 5, 
    icon: Heart,
    color: 'blue',
    campos: ['nombre_completo', 'telefono', 'cedula', 'correo', 'fecha_evento', 'observaciones']
  },
  { 
    value: 'novia', 
    label: 'Novia', 
    comision: 5, 
    icon: Heart,
    color: 'pink',
    campos: ['nombre_completo', 'telefono', 'cedula', 'correo', 'fecha_evento', 'observaciones']
  },
  { 
    value: 'papa_novio', 
    label: 'Papá del Novio', 
    comision: 3, 
    icon: User,
    color: 'indigo',
    campos: ['nombre_completo', 'telefono', 'cedula', 'relacion_evento', 'servicio_solicitado', 'observaciones']
  },
  { 
    value: 'papa_novia', 
    label: 'Papá de la Novia', 
    comision: 3, 
    icon: User,
    color: 'purple',
    campos: ['nombre_completo', 'telefono', 'cedula', 'relacion_evento', 'servicio_solicitado', 'observaciones']
  },
  { 
    value: 'invitado', 
    label: 'Invitado', 
    comision: 3, 
    icon: UserPlus,
    color: 'green',
    campos: ['nombre_completo', 'telefono', 'cedula', 'relacion_evento', 'servicio_solicitado', 'observaciones']
  },
  { 
    value: 'mascota', 
    label: 'Mascota', 
    comision: 3, 
    icon: Dog,
    color: 'amber',
    campos: ['nombre_mascota', 'tipo_mascota', 'raza', 'persona_asociada', 'servicio_solicitado', 'observaciones']
  },
  { 
    value: 'paje', 
    label: 'Paje', 
    comision: 3, 
    icon: Sparkles,
    color: 'cyan',
    campos: ['nombre_completo', 'telefono', 'relacion_evento', 'servicio_solicitado', 'observaciones']
  },
  { 
    value: 'corte_completa', 
    label: 'Corte Completa', 
    comision: 5, 
    icon: Users,
    color: 'orange',
    campos: ['descripcion', 'cantidad_personas', 'servicios_incluidos', 'observaciones']
  },
  { 
    value: 'otro', 
    label: 'Otro', 
    comision: 2, 
    icon: FileText,
    color: 'zinc',
    campos: ['descripcion', 'detalles', 'observaciones']
  }
];

const ESTADOS_REFERIDO = [
  { value: 'registrado', label: 'Registrado', color: 'zinc' },
  { value: 'pendiente_contacto', label: 'Pendiente de contacto', color: 'amber' },
  { value: 'contactado', label: 'Contactado', color: 'blue' },
  { value: 'cotizado', label: 'Cotizado', color: 'purple' },
  { value: 'venta_realizada', label: 'Venta realizada', color: 'green' },
  { value: 'no_fue_venta', label: 'No fue venta', color: 'red' },
  { value: 'listo_cobro', label: 'Listo para cobro', color: 'yellow' },
  { value: 'cuenta_cobro_generada', label: 'Cuenta de cobro generada', color: 'teal' },
  { value: 'pagado', label: 'Pagado', color: 'emerald' }
];

const ESTADOS_COMISION = [
  { value: 'pendiente', label: 'Pendiente', color: 'zinc' },
  { value: 'calculada', label: 'Calculada', color: 'blue' },
  { value: 'aprobada', label: 'Aprobada', color: 'green' },
  { value: 'cuenta_cobro_generada', label: 'En Cuenta de Cobro', color: 'teal' },
  { value: 'pagada', label: 'Pagada', color: 'emerald' }
];

// Configuración de IVA (19% Colombia)
const IVA_PORCENTAJE = 19;
const IVA_FACTOR = 1 + (IVA_PORCENTAJE / 100); // 1.19

// Funciones de cálculo
const calcularBaseSinIVA = (valorConIVA) => {
  return Math.round(valorConIVA / IVA_FACTOR);
};

const calcularIVA = (valorConIVA) => {
  const baseSinIVA = calcularBaseSinIVA(valorConIVA);
  return valorConIVA - baseSinIVA;
};

const calcularComision = (valorConIVA, porcentajeComision) => {
  const baseSinIVA = calcularBaseSinIVA(valorConIVA);
  return Math.round(baseSinIVA * (porcentajeComision / 100));
};

const formatPrice = (value) => {
  if (!value && value !== 0) return '$ 0';
  return `$ ${Math.round(value).toLocaleString('es-CO')}`;
};

// Componente para un Referido Interno - DARK THEME
const ReferidoInternoCard = ({ 
  interno, 
  index, 
  onUpdate, 
  onDelete, 
  isExpanded, 
  onToggleExpand,
  canEdit,
  canDelete 
}) => {
  const tipoConfig = TIPOS_REFERIDO_INTERNO.find(t => t.value === interno.tipo) || TIPOS_REFERIDO_INTERNO[8];
  const IconComponent = tipoConfig.icon;
  
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    ...interno,
    datos: interno.datos || {},
    // Calcular valor_total_con_iva a partir de valor_base_sin_iva si existe
    valor_total_con_iva: interno.valor_total_con_iva || Math.round((interno.valor_base_sin_iva || 0) * IVA_FACTOR)
  });

  useEffect(() => {
    setFormData({
      ...interno,
      datos: interno.datos || {},
      valor_total_con_iva: interno.valor_total_con_iva || Math.round((interno.valor_base_sin_iva || 0) * IVA_FACTOR)
    });
  }, [interno]);

  const handleSave = () => {
    const valorConIVA = formData.valor_total_con_iva || 0;
    const baseSinIVA = calcularBaseSinIVA(valorConIVA);
    const comision = calcularComision(valorConIVA, formData.porcentaje_comision);
    
    onUpdate(interno.id, {
      ...formData,
      valor_total_con_iva: valorConIVA,
      valor_base_sin_iva: baseSinIVA,
      comision_calculada: comision
    });
    setEditMode(false);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDatosChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      datos: {
        ...prev.datos,
        [field]: value
      }
    }));
  };

  // Renderizar campos según el tipo - DARK THEME
  const renderCamposFormulario = () => {
    const campos = tipoConfig.campos || [];
    const inputClass = "w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:ring-2 focus:ring-[#D4AF37] focus:border-[#D4AF37]";
    const labelClass = "block text-sm font-medium text-zinc-300 mb-1";
    
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        {campos.includes('nombre_completo') && (
          <div>
            <label className={labelClass}>Nombre Completo</label>
            <input
              type="text"
              value={formData.datos.nombre_completo || ''}
              onChange={(e) => handleDatosChange('nombre_completo', e.target.value)}
              className={inputClass}
              disabled={!editMode}
            />
          </div>
        )}
        
        {campos.includes('nombre_mascota') && (
          <div>
            <label className={labelClass}>Nombre de la Mascota</label>
            <input
              type="text"
              value={formData.datos.nombre_mascota || ''}
              onChange={(e) => handleDatosChange('nombre_mascota', e.target.value)}
              className={inputClass}
              disabled={!editMode}
            />
          </div>
        )}
        
        {campos.includes('tipo_mascota') && (
          <div>
            <label className={labelClass}>Tipo de Mascota</label>
            <select
              value={formData.datos.tipo_mascota || 'Perro'}
              onChange={(e) => handleDatosChange('tipo_mascota', e.target.value)}
              className={inputClass}
              disabled={!editMode}
            >
              <option value="Perro">Perro</option>
              <option value="Gato">Gato</option>
              <option value="Otro">Otro</option>
            </select>
          </div>
        )}
        
        {campos.includes('raza') && (
          <div>
            <label className={labelClass}>Raza</label>
            <input
              type="text"
              value={formData.datos.raza || ''}
              onChange={(e) => handleDatosChange('raza', e.target.value)}
              className={inputClass}
              disabled={!editMode}
            />
          </div>
        )}
        
        {campos.includes('persona_asociada') && (
          <div>
            <label className={labelClass}>Persona Asociada / Dueño</label>
            <input
              type="text"
              value={formData.datos.persona_asociada || ''}
              onChange={(e) => handleDatosChange('persona_asociada', e.target.value)}
              className={inputClass}
              disabled={!editMode}
            />
          </div>
        )}
        
        {campos.includes('telefono') && (
          <div>
            <label className={labelClass}>Teléfono</label>
            <input
              type="tel"
              value={formData.datos.telefono || ''}
              onChange={(e) => handleDatosChange('telefono', e.target.value)}
              className={inputClass}
              disabled={!editMode}
            />
          </div>
        )}
        
        {campos.includes('cedula') && (
          <div>
            <label className={labelClass}>Cédula</label>
            <input
              type="text"
              value={formData.datos.cedula || ''}
              onChange={(e) => handleDatosChange('cedula', e.target.value)}
              className={inputClass}
              disabled={!editMode}
            />
          </div>
        )}
        
        {campos.includes('correo') && (
          <div>
            <label className={labelClass}>Correo</label>
            <input
              type="email"
              value={formData.datos.correo || ''}
              onChange={(e) => handleDatosChange('correo', e.target.value)}
              className={inputClass}
              disabled={!editMode}
            />
          </div>
        )}
        
        {campos.includes('fecha_evento') && (
          <div>
            <label className={labelClass}>Fecha del Evento</label>
            <input
              type="date"
              value={formData.datos.fecha_evento || ''}
              onChange={(e) => handleDatosChange('fecha_evento', e.target.value)}
              className={inputClass}
              disabled={!editMode}
            />
          </div>
        )}
        
        {campos.includes('relacion_evento') && (
          <div>
            <label className={labelClass}>Relación con el Evento</label>
            <input
              type="text"
              value={formData.datos.relacion_evento || ''}
              onChange={(e) => handleDatosChange('relacion_evento', e.target.value)}
              placeholder="Ej: Papá del novio, Padrino, etc."
              className={inputClass}
              disabled={!editMode}
            />
          </div>
        )}
        
        {campos.includes('servicio_solicitado') && (
          <div>
            <label className={labelClass}>Servicio o Prenda Solicitada</label>
            <input
              type="text"
              value={formData.datos.servicio_solicitado || ''}
              onChange={(e) => handleDatosChange('servicio_solicitado', e.target.value)}
              className={inputClass}
              disabled={!editMode}
            />
          </div>
        )}
        
        {campos.includes('descripcion') && (
          <div className="md:col-span-2">
            <label className={labelClass}>Descripción</label>
            <input
              type="text"
              value={formData.datos.descripcion || ''}
              onChange={(e) => handleDatosChange('descripcion', e.target.value)}
              className={inputClass}
              disabled={!editMode}
            />
          </div>
        )}
        
        {campos.includes('cantidad_personas') && (
          <div>
            <label className={labelClass}>Cantidad de Personas</label>
            <input
              type="number"
              value={formData.datos.cantidad_personas || ''}
              onChange={(e) => handleDatosChange('cantidad_personas', parseInt(e.target.value) || 0)}
              className={inputClass}
              disabled={!editMode}
            />
          </div>
        )}
        
        {campos.includes('servicios_incluidos') && (
          <div className="md:col-span-2">
            <label className={labelClass}>Servicios Incluidos</label>
            <textarea
              value={formData.datos.servicios_incluidos || ''}
              onChange={(e) => handleDatosChange('servicios_incluidos', e.target.value)}
              rows={2}
              className={inputClass}
              disabled={!editMode}
            />
          </div>
        )}
        
        {campos.includes('detalles') && (
          <div className="md:col-span-2">
            <label className={labelClass}>Detalles</label>
            <textarea
              value={formData.datos.detalles || ''}
              onChange={(e) => handleDatosChange('detalles', e.target.value)}
              rows={2}
              className={inputClass}
              disabled={!editMode}
            />
          </div>
        )}
        
        {campos.includes('observaciones') && (
          <div className="md:col-span-2">
            <label className={labelClass}>Observaciones</label>
            <textarea
              value={formData.datos.observaciones || ''}
              onChange={(e) => handleDatosChange('observaciones', e.target.value)}
              rows={2}
              className={inputClass}
              disabled={!editMode}
            />
          </div>
        )}
        
        {/* Campos de comisión - NUEVA LÓGICA */}
        <div className="md:col-span-2 pt-4 border-t border-zinc-700">
          <h4 className="font-medium text-zinc-300 mb-3">Datos de Comisión</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Valor Total con IVA - EDITABLE */}
            <div>
              <label className={labelClass}>Valor Total con IVA</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400">$</span>
                <input
                  type="number"
                  value={formData.valor_total_con_iva || 0}
                  onChange={(e) => handleChange('valor_total_con_iva', parseFloat(e.target.value) || 0)}
                  className={`${inputClass} pl-8`}
                  disabled={!editMode}
                />
              </div>
            </div>
            
            {/* Base sin IVA - CALCULADO AUTOMÁTICO */}
            <div>
              <label className={labelClass}>Base sin IVA</label>
              <div className="px-3 py-2 bg-zinc-700/50 border border-zinc-600 rounded-lg text-zinc-300 font-medium">
                {formatPrice(calcularBaseSinIVA(formData.valor_total_con_iva || 0))}
              </div>
            </div>
            
            {/* IVA Calculado - AUTOMÁTICO */}
            <div>
              <label className={labelClass}>IVA ({IVA_PORCENTAJE}%)</label>
              <div className="px-3 py-2 bg-zinc-700/50 border border-zinc-600 rounded-lg text-zinc-400 font-medium">
                {formatPrice(calcularIVA(formData.valor_total_con_iva || 0))}
              </div>
            </div>
            
            {/* % Comisión - EDITABLE */}
            <div>
              <label className={labelClass}>% Comisión</label>
              <div className="relative">
                <input
                  type="number"
                  step="0.5"
                  value={formData.porcentaje_comision || 0}
                  onChange={(e) => handleChange('porcentaje_comision', parseFloat(e.target.value) || 0)}
                  className={`${inputClass} pr-8`}
                  disabled={!editMode}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400">%</span>
              </div>
            </div>
            
            {/* Comisión Calculada - AUTOMÁTICO */}
            <div>
              <label className={labelClass}>Comisión</label>
              <div className="px-3 py-2 bg-[#D4AF37]/20 border border-[#D4AF37]/50 rounded-lg text-[#D4AF37] font-semibold">
                {formatPrice(calcularComision(formData.valor_total_con_iva || 0, formData.porcentaje_comision || 0))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="border border-zinc-700 rounded-lg overflow-hidden bg-zinc-800/50">
      {/* Header */}
      <div 
        className="p-4 flex items-center justify-between cursor-pointer hover:bg-zinc-700/50 transition-colors"
        onClick={onToggleExpand}
      >
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-zinc-700">
            <IconComponent className="w-5 h-5 text-[#D4AF37]" />
          </div>
          <div>
            <h4 className="font-semibold text-white">
              {tipoConfig.label}
              {formData.datos.nombre_completo && ` - ${formData.datos.nombre_completo}`}
              {formData.datos.nombre_mascota && ` - ${formData.datos.nombre_mascota}`}
              {formData.datos.descripcion && ` - ${formData.datos.descripcion}`}
            </h4>
            <p className="text-sm text-zinc-400">
              Total: {formatPrice(formData.valor_total_con_iva || 0)} → Base: {formatPrice(calcularBaseSinIVA(formData.valor_total_con_iva || 0))} × {formData.porcentaje_comision}% = <span className="text-[#D4AF37]">{formatPrice(formData.comision_calculada || calcularComision(formData.valor_total_con_iva || 0, formData.porcentaje_comision || 0))}</span>
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {canEdit && !editMode && (
            <button
              onClick={(e) => { e.stopPropagation(); setEditMode(true); }}
              className="p-2 hover:bg-zinc-600 rounded-lg transition-colors text-zinc-400 hover:text-white"
              title="Editar"
            >
              <Edit2 className="w-4 h-4" />
            </button>
          )}
          {canDelete && (
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(interno.id); }}
              className="p-2 hover:bg-red-900/50 rounded-lg transition-colors text-zinc-400 hover:text-red-400"
              title="Eliminar"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
          {isExpanded ? <ChevronUp className="w-5 h-5 text-zinc-400" /> : <ChevronDown className="w-5 h-5 text-zinc-400" />}
        </div>
      </div>
      
      {/* Content */}
      {isExpanded && (
        <div className="p-4 bg-zinc-900/50 border-t border-zinc-700">
          {renderCamposFormulario()}
          
          {editMode && (
            <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-zinc-700">
              <button
                onClick={() => { setEditMode(false); setFormData({ ...interno, datos: interno.datos || {} }); }}
                className="px-4 py-2 text-zinc-400 hover:bg-zinc-700 rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-[#D4AF37] text-black rounded-lg hover:bg-[#C49F27] transition-colors flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                Guardar Cambios
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Modal para agregar nuevo referido interno - DARK THEME
const AgregarReferidoInternoModal = ({ isOpen, onClose, onAdd }) => {
  const [step, setStep] = useState(1);
  const [tipoSeleccionado, setTipoSeleccionado] = useState(null);
  const [formData, setFormData] = useState({
    datos: {},
    valor_total_con_iva: 0,
    porcentaje_comision: 5,
    observaciones: ''
  });

  useEffect(() => {
    if (!isOpen) {
      setStep(1);
      setTipoSeleccionado(null);
      setFormData({
        datos: {},
        valor_total_con_iva: 0,
        porcentaje_comision: 5,
        observaciones: ''
      });
    }
  }, [isOpen]);

  const handleSelectTipo = (tipo) => {
    setTipoSeleccionado(tipo);
    setFormData(prev => ({
      ...prev,
      porcentaje_comision: tipo.comision
    }));
    setStep(2);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDatosChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      datos: {
        ...prev.datos,
        [field]: value
      }
    }));
  };

  const handleSubmit = () => {
    const valorConIVA = formData.valor_total_con_iva || 0;
    const baseSinIVA = calcularBaseSinIVA(valorConIVA);
    const comision = calcularComision(valorConIVA, formData.porcentaje_comision);
    
    onAdd({
      tipo: tipoSeleccionado.value,
      datos: formData.datos,
      valor_total_con_iva: valorConIVA,
      valor_base_sin_iva: baseSinIVA,
      porcentaje_comision: formData.porcentaje_comision,
      comision_calculada: comision,
      observaciones: formData.observaciones
    });
    onClose();
  };

  if (!isOpen) return null;

  const inputClass = "w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:ring-2 focus:ring-[#D4AF37] focus:border-[#D4AF37]";
  const labelClass = "block text-sm font-medium text-zinc-300 mb-1";

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-zinc-900 border border-zinc-700 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-4 border-b border-zinc-700 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white">
            {step === 1 ? 'Seleccionar Tipo de Referido' : `Agregar ${tipoSeleccionado?.label}`}
          </h3>
          <button onClick={onClose} className="p-2 hover:bg-zinc-700 rounded-lg text-zinc-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-4">
          {step === 1 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {TIPOS_REFERIDO_INTERNO.map((tipo) => {
                const IconComponent = tipo.icon;
                return (
                  <button
                    key={tipo.value}
                    onClick={() => handleSelectTipo(tipo)}
                    className="p-4 border border-zinc-700 rounded-xl hover:border-[#D4AF37] hover:bg-zinc-800 transition-all text-left group"
                  >
                    <div className="p-3 rounded-lg bg-zinc-800 text-[#D4AF37] w-fit mb-2 group-hover:scale-110 transition-transform">
                      <IconComponent className="w-6 h-6" />
                    </div>
                    <h4 className="font-medium text-white">{tipo.label}</h4>
                    <p className="text-sm text-zinc-400">{tipo.comision}% comisión</p>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="space-y-4">
              {/* Campos según el tipo */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {tipoSeleccionado?.campos.includes('nombre_completo') && (
                  <div>
                    <label className={labelClass}>Nombre Completo *</label>
                    <input
                      type="text"
                      value={formData.datos.nombre_completo || ''}
                      onChange={(e) => handleDatosChange('nombre_completo', e.target.value)}
                      className={inputClass}
                      required
                    />
                  </div>
                )}
                
                {tipoSeleccionado?.campos.includes('nombre_mascota') && (
                  <div>
                    <label className={labelClass}>Nombre de la Mascota *</label>
                    <input
                      type="text"
                      value={formData.datos.nombre_mascota || ''}
                      onChange={(e) => handleDatosChange('nombre_mascota', e.target.value)}
                      className={inputClass}
                      required
                    />
                  </div>
                )}
                
                {tipoSeleccionado?.campos.includes('tipo_mascota') && (
                  <div>
                    <label className={labelClass}>Tipo de Mascota</label>
                    <select
                      value={formData.datos.tipo_mascota || 'Perro'}
                      onChange={(e) => handleDatosChange('tipo_mascota', e.target.value)}
                      className={inputClass}
                    >
                      <option value="Perro">Perro</option>
                      <option value="Gato">Gato</option>
                      <option value="Otro">Otro</option>
                    </select>
                  </div>
                )}
                
                {tipoSeleccionado?.campos.includes('raza') && (
                  <div>
                    <label className={labelClass}>Raza</label>
                    <input
                      type="text"
                      value={formData.datos.raza || ''}
                      onChange={(e) => handleDatosChange('raza', e.target.value)}
                      className={inputClass}
                    />
                  </div>
                )}
                
                {tipoSeleccionado?.campos.includes('persona_asociada') && (
                  <div>
                    <label className={labelClass}>Persona Asociada / Dueño</label>
                    <input
                      type="text"
                      value={formData.datos.persona_asociada || ''}
                      onChange={(e) => handleDatosChange('persona_asociada', e.target.value)}
                      className={inputClass}
                    />
                  </div>
                )}
                
                {tipoSeleccionado?.campos.includes('telefono') && (
                  <div>
                    <label className={labelClass}>Teléfono</label>
                    <input
                      type="tel"
                      value={formData.datos.telefono || ''}
                      onChange={(e) => handleDatosChange('telefono', e.target.value)}
                      className={inputClass}
                    />
                  </div>
                )}
                
                {tipoSeleccionado?.campos.includes('cedula') && (
                  <div>
                    <label className={labelClass}>Cédula</label>
                    <input
                      type="text"
                      value={formData.datos.cedula || ''}
                      onChange={(e) => handleDatosChange('cedula', e.target.value)}
                      className={inputClass}
                    />
                  </div>
                )}
                
                {tipoSeleccionado?.campos.includes('correo') && (
                  <div>
                    <label className={labelClass}>Correo</label>
                    <input
                      type="email"
                      value={formData.datos.correo || ''}
                      onChange={(e) => handleDatosChange('correo', e.target.value)}
                      className={inputClass}
                    />
                  </div>
                )}
                
                {tipoSeleccionado?.campos.includes('fecha_evento') && (
                  <div>
                    <label className={labelClass}>Fecha del Evento</label>
                    <input
                      type="date"
                      value={formData.datos.fecha_evento || ''}
                      onChange={(e) => handleDatosChange('fecha_evento', e.target.value)}
                      className={inputClass}
                    />
                  </div>
                )}
                
                {tipoSeleccionado?.campos.includes('relacion_evento') && (
                  <div>
                    <label className={labelClass}>Relación con el Evento</label>
                    <input
                      type="text"
                      value={formData.datos.relacion_evento || ''}
                      onChange={(e) => handleDatosChange('relacion_evento', e.target.value)}
                      placeholder="Ej: Papá del novio, Padrino, etc."
                      className={inputClass}
                    />
                  </div>
                )}
                
                {tipoSeleccionado?.campos.includes('servicio_solicitado') && (
                  <div>
                    <label className={labelClass}>Servicio o Prenda Solicitada</label>
                    <input
                      type="text"
                      value={formData.datos.servicio_solicitado || ''}
                      onChange={(e) => handleDatosChange('servicio_solicitado', e.target.value)}
                      className={inputClass}
                    />
                  </div>
                )}
                
                {tipoSeleccionado?.campos.includes('descripcion') && (
                  <div className="md:col-span-2">
                    <label className={labelClass}>Descripción *</label>
                    <input
                      type="text"
                      value={formData.datos.descripcion || ''}
                      onChange={(e) => handleDatosChange('descripcion', e.target.value)}
                      className={inputClass}
                      required
                    />
                  </div>
                )}
                
                {tipoSeleccionado?.campos.includes('cantidad_personas') && (
                  <div>
                    <label className={labelClass}>Cantidad de Personas</label>
                    <input
                      type="number"
                      value={formData.datos.cantidad_personas || ''}
                      onChange={(e) => handleDatosChange('cantidad_personas', parseInt(e.target.value) || 0)}
                      className={inputClass}
                    />
                  </div>
                )}
                
                {tipoSeleccionado?.campos.includes('servicios_incluidos') && (
                  <div className="md:col-span-2">
                    <label className={labelClass}>Servicios Incluidos</label>
                    <textarea
                      value={formData.datos.servicios_incluidos || ''}
                      onChange={(e) => handleDatosChange('servicios_incluidos', e.target.value)}
                      rows={2}
                      className={inputClass}
                    />
                  </div>
                )}
                
                {tipoSeleccionado?.campos.includes('detalles') && (
                  <div className="md:col-span-2">
                    <label className={labelClass}>Detalles</label>
                    <textarea
                      value={formData.datos.detalles || ''}
                      onChange={(e) => handleDatosChange('detalles', e.target.value)}
                      rows={2}
                      className={inputClass}
                    />
                  </div>
                )}
              </div>
              
              {/* Campos de comisión - NUEVA LÓGICA */}
              <div className="pt-4 border-t border-zinc-700">
                <h4 className="font-medium text-zinc-300 mb-3">Datos de Comisión</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                  {/* Valor Total con IVA - EDITABLE */}
                  <div>
                    <label className={labelClass}>Valor Total con IVA</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400">$</span>
                      <input
                        type="number"
                        value={formData.valor_total_con_iva || 0}
                        onChange={(e) => handleChange('valor_total_con_iva', parseFloat(e.target.value) || 0)}
                        className={`${inputClass} pl-8`}
                        placeholder="Ej: 5925000"
                      />
                    </div>
                  </div>
                  
                  {/* Base sin IVA - CALCULADO AUTOMÁTICO */}
                  <div>
                    <label className={labelClass}>Base sin IVA</label>
                    <div className="px-3 py-2 bg-zinc-700/50 border border-zinc-600 rounded-lg text-zinc-300 font-medium">
                      {formatPrice(calcularBaseSinIVA(formData.valor_total_con_iva || 0))}
                    </div>
                  </div>
                  
                  {/* IVA Calculado - AUTOMÁTICO */}
                  <div>
                    <label className={labelClass}>IVA ({IVA_PORCENTAJE}%)</label>
                    <div className="px-3 py-2 bg-zinc-700/50 border border-zinc-600 rounded-lg text-zinc-400 font-medium">
                      {formatPrice(calcularIVA(formData.valor_total_con_iva || 0))}
                    </div>
                  </div>
                  
                  {/* % Comisión - EDITABLE */}
                  <div>
                    <label className={labelClass}>% Comisión</label>
                    <div className="relative">
                      <input
                        type="number"
                        step="0.5"
                        value={formData.porcentaje_comision || 0}
                        onChange={(e) => handleChange('porcentaje_comision', parseFloat(e.target.value) || 0)}
                        className={`${inputClass} pr-8`}
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400">%</span>
                    </div>
                  </div>
                  
                  {/* Comisión Calculada - AUTOMÁTICO */}
                  <div>
                    <label className={labelClass}>Comisión</label>
                    <div className="px-3 py-2 bg-[#D4AF37]/20 border border-[#D4AF37]/50 rounded-lg text-[#D4AF37] font-semibold">
                      {formatPrice(calcularComision(formData.valor_total_con_iva || 0, formData.porcentaje_comision || 0))}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Observaciones */}
              <div>
                <label className={labelClass}>Observaciones</label>
                <textarea
                  value={formData.observaciones || ''}
                  onChange={(e) => handleChange('observaciones', e.target.value)}
                  rows={2}
                  className={inputClass}
                />
              </div>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="p-4 border-t border-zinc-700 flex justify-between">
          {step === 2 && (
            <button
              onClick={() => setStep(1)}
              className="px-4 py-2 text-zinc-400 hover:bg-zinc-700 rounded-lg transition-colors"
            >
              ← Cambiar Tipo
            </button>
          )}
          <div className="flex gap-2 ml-auto">
            <button
              onClick={onClose}
              className="px-4 py-2 text-zinc-400 hover:bg-zinc-700 rounded-lg transition-colors"
            >
              Cancelar
            </button>
            {step === 2 && (
              <button
                onClick={handleSubmit}
                className="px-4 py-2 bg-[#D4AF37] text-black rounded-lg hover:bg-[#C49F27] transition-colors flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Agregar
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Componente principal - DARK THEME
const ReferidoFormV2 = () => {
  const navigate = useNavigate();
  const { aliado_id, id } = useParams();
  const { user } = useAuth();
  const isEditing = Boolean(id);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [aliado, setAliado] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [expandedInternos, setExpandedInternos] = useState({});
  
  // Form data para el referido principal
  const [formData, setFormData] = useState({
    nombre_referencia: '',
    fecha_evento: '',
    observaciones_generales: '',
    estado: 'registrado',
    estado_comision: 'pendiente',
    referidos_internos: []
  });

  const canEdit = user?.role === 'admin' || user?.role === 'gerencia';
  const canDelete = user?.role === 'admin' || user?.role === 'gerencia';

  // Cargar datos
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        
        // Cargar aliado
        const aliadoRes = await axios.get(`${API_URL}/api/aliados/${aliado_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAliado(aliadoRes.data);
        
        // Si estamos editando, cargar referido
        if (isEditing) {
          const referidoRes = await axios.get(`${API_URL}/api/aliados/referidos/${id}/internos`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          // Cargar el referido completo
          const allReferidosRes = await axios.get(`${API_URL}/api/aliados/${aliado_id}/referidos`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          const referido = allReferidosRes.data.referidos.find(r => r.id === id);
          
          if (referido) {
            setFormData({
              nombre_referencia: referido.nombre_referencia || referido.nombre || '',
              fecha_evento: referido.fecha_evento || '',
              observaciones_generales: referido.observaciones || '',
              estado: referido.estado || 'registrado',
              estado_comision: referido.estado_comision || 'pendiente',
              referidos_internos: referido.referidos_internos || []
            });
            
            // Expandir el primer interno por defecto
            if (referido.referidos_internos?.length > 0) {
              setExpandedInternos({ [referido.referidos_internos[0].id]: true });
            }
          }
        }
      } catch (error) {
        console.error('Error cargando datos:', error);
        toast.error('Error al cargar los datos');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [aliado_id, id, isEditing]);

  // Calcular totales
  const calcularTotales = useCallback(() => {
    const internos = formData.referidos_internos || [];
    const valorTotalConIVA = internos.reduce((sum, ri) => sum + (ri.valor_total_con_iva || Math.round((ri.valor_base_sin_iva || 0) * IVA_FACTOR)), 0);
    const valorBaseSinIVA = calcularBaseSinIVA(valorTotalConIVA);
    const ivaTotal = calcularIVA(valorTotalConIVA);
    const comisionTotal = internos.reduce((sum, ri) => {
      const valorConIVA = ri.valor_total_con_iva || Math.round((ri.valor_base_sin_iva || 0) * IVA_FACTOR);
      return sum + calcularComision(valorConIVA, ri.porcentaje_comision || 0);
    }, 0);
    return { valorTotalConIVA, valorBaseSinIVA, ivaTotal, comisionTotal };
  }, [formData.referidos_internos]);

  const { valorTotalConIVA, valorBaseSinIVA, ivaTotal, comisionTotal } = calcularTotales();

  // Agregar referido interno
  const handleAddInterno = async (internoData) => {
    try {
      const token = localStorage.getItem('token');
      
      if (isEditing) {
        // Si estamos editando, guardar en el servidor
        const response = await axios.post(
          `${API_URL}/api/aliados/referidos/${id}/internos`,
          internoData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Actualizar estado local
        setFormData(prev => ({
          ...prev,
          referidos_internos: [...prev.referidos_internos, response.data.referido_interno]
        }));
        
        toast.success(`${internoData.tipo} agregado correctamente`);
      } else {
        // Si es nuevo, solo agregar al estado local
        const nuevoInterno = {
          ...internoData,
          id: `temp-${Date.now()}`,
          created_at: new Date().toISOString()
        };
        
        setFormData(prev => ({
          ...prev,
          referidos_internos: [...prev.referidos_internos, nuevoInterno]
        }));
      }
    } catch (error) {
      console.error('Error agregando referido interno:', error);
      toast.error('Error al agregar el referido interno');
    }
  };

  // Actualizar referido interno
  const handleUpdateInterno = async (internoId, internoData) => {
    try {
      const token = localStorage.getItem('token');
      
      if (isEditing && !internoId.startsWith('temp-')) {
        // Si estamos editando y existe en el servidor, actualizar
        await axios.put(
          `${API_URL}/api/aliados/referidos/${id}/internos/${internoId}`,
          internoData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Referido interno actualizado');
      }
      
      // Actualizar estado local
      setFormData(prev => ({
        ...prev,
        referidos_internos: prev.referidos_internos.map(ri => 
          ri.id === internoId ? { ...ri, ...internoData } : ri
        )
      }));
    } catch (error) {
      console.error('Error actualizando referido interno:', error);
      toast.error('Error al actualizar el referido interno');
    }
  };

  // Eliminar referido interno
  const handleDeleteInterno = async (internoId) => {
    if (!window.confirm('¿Estás seguro de eliminar este referido interno?')) return;
    
    try {
      const token = localStorage.getItem('token');
      
      if (isEditing && !internoId.startsWith('temp-')) {
        // Si existe en el servidor, eliminar
        await axios.delete(
          `${API_URL}/api/aliados/referidos/${id}/internos/${internoId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Referido interno eliminado');
      }
      
      // Actualizar estado local
      setFormData(prev => ({
        ...prev,
        referidos_internos: prev.referidos_internos.filter(ri => ri.id !== internoId)
      }));
    } catch (error) {
      console.error('Error eliminando referido interno:', error);
      toast.error('Error al eliminar el referido interno');
    }
  };

  // Guardar referido principal
  const handleSave = async () => {
    if (!formData.nombre_referencia.trim()) {
      toast.error('El nombre de referencia es requerido');
      return;
    }
    
    if (formData.referidos_internos.length === 0) {
      toast.error('Debe agregar al menos un referido interno');
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      if (isEditing) {
        // Actualizar referido existente
        await axios.put(
          `${API_URL}/api/aliados/referidos/${id}`,
          {
            nombre: formData.nombre_referencia,
            nombre_referencia: formData.nombre_referencia,
            fecha_evento: formData.fecha_evento,
            observaciones: formData.observaciones_generales,
            estado: formData.estado,
            estado_comision: formData.estado_comision
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        toast.success('Referido actualizado correctamente');
      } else {
        // Crear nuevo referido con estructura nueva
        const primerInterno = formData.referidos_internos[0];
        const nombrePrincipal = primerInterno?.datos?.nombre_completo || 
                               primerInterno?.datos?.nombre_mascota || 
                               primerInterno?.datos?.descripcion || 
                               formData.nombre_referencia;
        
        const nuevoReferidoRes = await axios.post(
          `${API_URL}/api/aliados/${aliado_id}/referidos`,
          {
            tipo_referido: primerInterno?.tipo || 'otro',
            nombre: nombrePrincipal,
            telefono: primerInterno?.datos?.telefono || '',
            cedula: primerInterno?.datos?.cedula || '',
            correo: primerInterno?.datos?.correo || '',
            fecha_evento: formData.fecha_evento,
            observaciones: formData.observaciones_generales
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        const nuevoReferidoId = nuevoReferidoRes.data.id;
        
        // Actualizar con nombre_referencia y agregar referidos internos
        await axios.put(
          `${API_URL}/api/aliados/referidos/${nuevoReferidoId}`,
          {
            nombre_referencia: formData.nombre_referencia
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Agregar cada referido interno
        for (const interno of formData.referidos_internos) {
          await axios.post(
            `${API_URL}/api/aliados/referidos/${nuevoReferidoId}/internos`,
            {
              tipo: interno.tipo,
              datos: interno.datos,
              valor_base_sin_iva: interno.valor_base_sin_iva,
              porcentaje_comision: interno.porcentaje_comision,
              observaciones: interno.observaciones
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        }
        
        toast.success('Referido creado correctamente');
      }
      
      navigate(`/aliados/${aliado_id}`);
    } catch (error) {
      console.error('Error guardando referido:', error);
      toast.error('Error al guardar el referido');
    } finally {
      setSaving(false);
    }
  };

  const toggleExpand = (internoId) => {
    setExpandedInternos(prev => ({
      ...prev,
      [internoId]: !prev[internoId]
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  const inputClass = "w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:ring-2 focus:ring-[#D4AF37] focus:border-[#D4AF37]";
  const labelClass = "block text-sm font-medium text-zinc-300 mb-1";

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Header */}
      <div className="bg-black border-b border-zinc-800 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(`/aliados/${aliado_id}`)}
                className="p-2 hover:bg-zinc-800 rounded-lg transition-colors text-zinc-400 hover:text-white"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl font-semibold text-white">
                  {isEditing ? 'Editar Referido' : 'Nuevo Referido'}
                </h1>
                <p className="text-sm text-zinc-400">
                  Aliado: {aliado?.nombre}
                </p>
              </div>
            </div>
            
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-[#D4AF37] text-black rounded-lg hover:bg-[#C49F27] transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Guardar
            </button>
          </div>
        </div>
      </div>
      
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* Información del Referido Principal */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Heart className="w-5 h-5 text-[#D4AF37]" />
            Información del Referido Principal
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className={labelClass}>
                Nombre de Referencia / Evento *
              </label>
              <input
                type="text"
                value={formData.nombre_referencia}
                onChange={(e) => setFormData(prev => ({ ...prev, nombre_referencia: e.target.value }))}
                placeholder="Ej: Boda Ahmed Jessen, Evento Corporativo XYZ"
                className={inputClass}
              />
            </div>
            
            <div>
              <label className={labelClass}>
                Fecha del Evento
              </label>
              <input
                type="date"
                value={formData.fecha_evento}
                onChange={(e) => setFormData(prev => ({ ...prev, fecha_evento: e.target.value }))}
                className={inputClass}
              />
            </div>
            
            <div>
              <label className={labelClass}>
                Estado
              </label>
              <select
                value={formData.estado}
                onChange={(e) => setFormData(prev => ({ ...prev, estado: e.target.value }))}
                className={inputClass}
              >
                {ESTADOS_REFERIDO.map(est => (
                  <option key={est.value} value={est.value}>{est.label}</option>
                ))}
              </select>
            </div>
            
            {canEdit && (
              <div>
                <label className={labelClass}>
                  Estado de Comisión
                </label>
                <select
                  value={formData.estado_comision}
                  onChange={(e) => setFormData(prev => ({ ...prev, estado_comision: e.target.value }))}
                  className={inputClass}
                >
                  {ESTADOS_COMISION.map(est => (
                    <option key={est.value} value={est.value}>{est.label}</option>
                  ))}
                </select>
              </div>
            )}
            
            <div className="md:col-span-2">
              <label className={labelClass}>
                Observaciones Generales
              </label>
              <textarea
                value={formData.observaciones_generales}
                onChange={(e) => setFormData(prev => ({ ...prev, observaciones_generales: e.target.value }))}
                rows={2}
                className={inputClass}
              />
            </div>
          </div>
        </div>
        
        {/* Referidos Internos */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-[#D4AF37]" />
              Referidos Internos / Conceptos
              <span className="text-sm font-normal text-zinc-400">
                ({formData.referidos_internos.length})
              </span>
            </h2>
            
            <button
              onClick={() => setShowAddModal(true)}
              className="px-4 py-2 bg-[#D4AF37] text-black rounded-lg hover:bg-[#C49F27] transition-colors flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Agregar Referido Interno
            </button>
          </div>
          
          {formData.referidos_internos.length === 0 ? (
            <div className="text-center py-12 border-2 border-dashed border-zinc-700 rounded-xl">
              <Users className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
              <p className="text-zinc-400 mb-4">
                No hay referidos internos agregados
              </p>
              <button
                onClick={() => setShowAddModal(true)}
                className="px-4 py-2 bg-zinc-800 text-[#D4AF37] rounded-lg hover:bg-zinc-700 transition-colors inline-flex items-center gap-2 border border-[#D4AF37]/30"
              >
                <Plus className="w-4 h-4" />
                Agregar el primer referido
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {formData.referidos_internos.map((interno, index) => (
                <ReferidoInternoCard
                  key={interno.id}
                  interno={interno}
                  index={index}
                  onUpdate={handleUpdateInterno}
                  onDelete={handleDeleteInterno}
                  isExpanded={expandedInternos[interno.id]}
                  onToggleExpand={() => toggleExpand(interno.id)}
                  canEdit={canEdit}
                  canDelete={canDelete}
                />
              ))}
            </div>
          )}
        </div>
        
        {/* Resumen de Comisiones */}
        {formData.referidos_internos.length > 0 && (
          <div className="bg-gradient-to-br from-[#D4AF37]/10 to-[#D4AF37]/5 rounded-xl p-6 border border-[#D4AF37]/30">
            <h2 className="text-lg font-semibold text-[#D4AF37] mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              Resumen de Comisiones
            </h2>
            
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="bg-black/30 rounded-lg p-4">
                <p className="text-sm text-zinc-400 mb-1">Referidos</p>
                <p className="text-2xl font-bold text-white">{formData.referidos_internos.length}</p>
              </div>
              
              <div className="bg-black/30 rounded-lg p-4">
                <p className="text-sm text-zinc-400 mb-1">Total con IVA</p>
                <p className="text-xl font-bold text-white">{formatPrice(valorTotalConIVA)}</p>
              </div>
              
              <div className="bg-black/30 rounded-lg p-4">
                <p className="text-sm text-zinc-400 mb-1">Base sin IVA</p>
                <p className="text-xl font-bold text-zinc-300">{formatPrice(valorBaseSinIVA)}</p>
              </div>
              
              <div className="bg-black/30 rounded-lg p-4">
                <p className="text-sm text-zinc-400 mb-1">IVA ({IVA_PORCENTAJE}%)</p>
                <p className="text-xl font-bold text-zinc-400">{formatPrice(ivaTotal)}</p>
              </div>
              
              <div className="bg-black/30 rounded-lg p-4">
                <p className="text-sm text-zinc-400 mb-1">Comisión Total</p>
                <p className="text-2xl font-bold text-[#D4AF37]">{formatPrice(comisionTotal)}</p>
              </div>
            </div>
            
            {/* Desglose */}
            <div className="mt-4 pt-4 border-t border-[#D4AF37]/20">
              <h3 className="text-sm font-medium text-zinc-400 mb-2">Desglose por Referido:</h3>
              <div className="space-y-1">
                {formData.referidos_internos.map((interno, index) => {
                  const tipoConfig = TIPOS_REFERIDO_INTERNO.find(t => t.value === interno.tipo);
                  const valorConIVA = interno.valor_total_con_iva || Math.round((interno.valor_base_sin_iva || 0) * IVA_FACTOR);
                  const baseSinIVA = calcularBaseSinIVA(valorConIVA);
                  const comision = calcularComision(valorConIVA, interno.porcentaje_comision || 0);
                  return (
                    <div key={interno.id} className="flex justify-between text-sm">
                      <span className="text-zinc-300">
                        {tipoConfig?.label || interno.tipo}
                        {interno.datos?.nombre_completo && ` - ${interno.datos.nombre_completo}`}
                        {interno.datos?.nombre_mascota && ` - ${interno.datos.nombre_mascota}`}
                      </span>
                      <span className="text-[#D4AF37] font-medium">
                        {formatPrice(valorConIVA)} → {formatPrice(baseSinIVA)} × {interno.porcentaje_comision}% = {formatPrice(comision)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Modal para agregar referido interno */}
      <AgregarReferidoInternoModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAdd={handleAddInterno}
      />
    </div>
  );
};

export default ReferidoFormV2;
